import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import {
  Car,
  Clock,
  DollarSign,
  TrendingUp,
  Users,
  CheckSquare,
  Calendar,
  Target,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  Phone,
  Mail,
  Home,
  Bell,
  ClipboardList,
} from 'lucide-react'
import { formatCurrency, formatDate, formatNumber } from '../../utils/format'
import { card, Panel, PanelLink, EmptyState, StatCard } from '../../design-system'

// ==========================================
// INTERFACES
// ==========================================

interface DashboardCompleto {
  veiculosNoEstoque: number
  veiculosProprios: number
  veiculosConsignados: number
  mediaDiasEstoque: number
  vendasMesAtual: number
  vendasMesAnterior: number
  faturamentoMesAtual: number
  faturamentoMesAnterior: number
  avaliacoesPendentes: number
  aniversariantesMes: number
  clientesCadastrados: number
  leadsAtivos: number
  veiculosRecentes: VeiculoRecente[]
  tempoEstoque: TempoEstoque[]
  rankingVendedores: RankingVendedor[]
  tarefasPendentes: TarefaPendente[]
  contadores: Contadores
  leadsSemContato: LeadSemContato[]
}

interface VeiculoRecente {
  id: number
  placa: string | null
  marca: string
  modelo: string
  valorVenda: number
}

interface TempoEstoque {
  periodo: string
  cor: string
  quantidade: number
}

interface RankingVendedor {
  vendedorId: number
  nome: string
  vendasMesAtual: number
  vendasMesAnterior: number
}

interface TarefaPendente {
  id: number
  titulo: string
  tipo: string | null
  prioridade: string
  dataVencimento: string
}

interface Contadores {
  pendentes: number
  atrasadas: number
  paraHoje: number
  concluidasHoje: number
}

interface LeadSemContato {
  leadId: number
  leadNome: string
  diasParado: number
  etapaNome: string
  etapaCor: string
}

// ==========================================
// ITENS DE LISTA
// ==========================================

const tipoIcons: Record<string, React.ElementType> = {
  LIGACAO: Phone,
  EMAIL: Mail,
  REUNIAO: Users,
  VISITA: Home,
  TEST_DRIVE: Car,
  FOLLOW_UP: Bell,
  OUTRO: ClipboardList,
}

const TarefaItem = memo(function TarefaItem({
  tarefa,
  onClick,
}: {
  tarefa: TarefaPendente
  onClick: () => void
}) {
  const Icon = (tarefa.tipo && tipoIcons[tarefa.tipo]) || ClipboardList
  const prioStyle =
    tarefa.prioridade === 'URGENTE'
      ? 'bg-rose-50 text-rose-600'
      : tarefa.prioridade === 'ALTA'
        ? 'bg-amber-50 text-amber-600'
        : 'bg-slate-100 text-slate-500'

  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center gap-3 rounded-xl border border-transparent p-2.5 text-left transition-colors hover:border-slate-200 hover:bg-slate-50"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors group-hover:text-primary-600">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800">{tarefa.titulo}</p>
        <p className="text-xs text-slate-400">Vence {formatDate(tarefa.dataVencimento)}</p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${prioStyle}`}
      >
        {tarefa.prioridade}
      </span>
    </button>
  )
})

const LeadItem = memo(function LeadItem({
  lead,
  onClick,
}: {
  lead: LeadSemContato
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-transparent p-2.5 text-left transition-colors hover:border-slate-200 hover:bg-slate-50"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-800">{lead.leadNome}</p>
        <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: lead.etapaCor }} />
          {lead.etapaNome}
        </span>
      </div>
      <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-600">
        {lead.diasParado}d
      </span>
    </button>
  )
})

// Escala semântica de envelhecimento de estoque (verde -> vermelho)
const agingColors = ['#10b981', '#0ea5e9', '#f59e0b', '#f97316', '#ef4444']

const tempoEstoqueDefault: TempoEstoque[] = [
  { periodo: 'Até 30 dias', quantidade: 0, cor: '' },
  { periodo: '31 a 60 dias', quantidade: 0, cor: '' },
  { periodo: '61 a 90 dias', quantidade: 0, cor: '' },
  { periodo: '91 a 120 dias', quantidade: 0, cor: '' },
  { periodo: '+120 dias', quantidade: 0, cor: '' },
]

// ==========================================
// SKELETON
// ==========================================

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-9 w-56 animate-pulse rounded-lg bg-slate-200/70" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`${card} p-5`}>
            <div className="flex justify-between">
              <div className="h-3 w-20 animate-pulse rounded bg-slate-200/70" />
              <div className="h-9 w-9 animate-pulse rounded-xl bg-slate-200/70" />
            </div>
            <div className="mt-4 h-7 w-24 animate-pulse rounded bg-slate-200/70" />
            <div className="mt-3 h-3 w-28 animate-pulse rounded bg-slate-200/70" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={`${card} h-72 animate-pulse`} />
        ))}
      </div>
    </div>
  )
}

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardCompleto | null>(null)
  const [loading, setLoading] = useState(true)
  const [periodo, setPeriodo] = useState('MES_ATUAL')

  const loadData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true)
    try {
      const response = await api.get<DashboardCompleto>('/dashboard/completo', { signal })
      setData(response.data)
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') {
        return
      }
      console.error('Erro ao carregar dashboard:', error)
      setData({
        veiculosNoEstoque: 0,
        veiculosProprios: 0,
        veiculosConsignados: 0,
        mediaDiasEstoque: 0,
        vendasMesAtual: 0,
        vendasMesAnterior: 0,
        faturamentoMesAtual: 0,
        faturamentoMesAnterior: 0,
        avaliacoesPendentes: 0,
        aniversariantesMes: 0,
        clientesCadastrados: 0,
        leadsAtivos: 0,
        veiculosRecentes: [],
        tempoEstoque: [],
        rankingVendedores: [],
        tarefasPendentes: [],
        contadores: { pendentes: 0, atrasadas: 0, paraHoje: 0, concluidasHoje: 0 },
        leadsSemContato: [],
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadData(controller.signal)
    return () => controller.abort()
  }, [loadData])

  const trends = useMemo(
    () => ({
      vendas: calcularTrend(data?.vendasMesAtual || 0, data?.vendasMesAnterior || 0),
      faturamento: calcularTrend(data?.faturamentoMesAtual || 0, data?.faturamentoMesAnterior || 0),
    }),
    [data?.vendasMesAtual, data?.vendasMesAnterior, data?.faturamentoMesAtual, data?.faturamentoMesAnterior]
  )

  const dataAtual = useMemo(
    () =>
      new Date().toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
    []
  )

  const buckets = data?.tempoEstoque?.length ? data.tempoEstoque : tempoEstoqueDefault
  const maxBucket = useMemo(
    () => Math.max(...buckets.map((b) => b.quantidade), 1),
    [buckets]
  )

  const handleRefresh = useCallback(() => {
    loadData()
  }, [loadData])

  if (loading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-0.5 text-sm capitalize text-slate-400">
            Bem-vindo, {user?.nome} · {dataAtual}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="h-10 cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white pl-3.5 pr-9 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="HOJE">Hoje</option>
              <option value="SEMANA">Esta Semana</option>
              <option value="MES_ATUAL">Este Mês</option>
              <option value="TRIMESTRE">Trimestre</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
          <button
            onClick={handleRefresh}
            title="Atualizar"
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats Grid (padronizado) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Veículos no Estoque"
          value={data?.veiculosNoEstoque || 0}
          subtitle={`Próprios: ${data?.veiculosProprios || 0} · Consig.: ${data?.veiculosConsignados || 0}`}
          icon={<Car className="h-[18px] w-[18px]" />}
          onClick={() => navigate('/veiculos')}
          delay={0}
        />
        <StatCard
          title="Vendas Mês Atual"
          value={data?.vendasMesAtual || 0}
          subtitle={`Mês anterior: ${data?.vendasMesAnterior || 0}`}
          icon={<TrendingUp className="h-[18px] w-[18px]" />}
          trend={trends.vendas}
          onClick={() => navigate('/vendas')}
          delay={60}
        />
        <StatCard
          title="Faturamento"
          value={formatCurrency(data?.faturamentoMesAtual || 0)}
          subtitle={`Anterior: ${formatCurrency(data?.faturamentoMesAnterior || 0)}`}
          icon={<DollarSign className="h-[18px] w-[18px]" />}
          trend={trends.faturamento}
          onClick={() => navigate('/relatorios')}
          delay={120}
        />
        <StatCard
          title="Leads Ativos"
          value={data?.leadsAtivos || 0}
          subtitle="no funil de vendas"
          icon={<Target className="h-[18px] w-[18px]" />}
          onClick={() => navigate('/crm/leads')}
          delay={180}
        />
        <StatCard
          title="Tarefas Pendentes"
          value={data?.contadores?.pendentes || 0}
          subtitle={data?.contadores?.atrasadas ? `${data.contadores.atrasadas} atrasadas` : 'tudo em dia'}
          icon={<CheckSquare className="h-[18px] w-[18px]" />}
          alert={!!data?.contadores?.atrasadas}
          onClick={() => navigate('/crm/tarefas')}
          delay={240}
        />
        <StatCard
          title="Média Dias Estoque"
          value={`${data?.mediaDiasEstoque || 0} dias`}
          icon={<Clock className="h-[18px] w-[18px]" />}
          delay={300}
        />
        <StatCard
          title="Clientes"
          value={formatNumber(data?.clientesCadastrados || 0, 0)}
          subtitle="cadastrados"
          icon={<Users className="h-[18px] w-[18px]" />}
          onClick={() => navigate('/clientes')}
          delay={360}
        />
        <StatCard
          title="Tarefas Hoje"
          value={data?.contadores?.paraHoje || 0}
          subtitle={`${data?.contadores?.concluidasHoje || 0} concluídas`}
          icon={<Calendar className="h-[18px] w-[18px]" />}
          onClick={() => navigate('/agenda')}
          delay={420}
        />
      </div>

      {/* Grid de widgets */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Próximas Tarefas */}
        <Panel
          title="Próximas Tarefas"
          icon={<CheckSquare className="h-4 w-4" />}
          action={<PanelLink onClick={() => navigate('/crm/tarefas')}>Ver todas</PanelLink>}
          delay={460}
        >
          <div className="space-y-1.5">
            {data?.tarefasPendentes && data.tarefasPendentes.length > 0 ? (
              data.tarefasPendentes.map((tarefa) => (
                <TarefaItem
                  key={tarefa.id}
                  tarefa={tarefa}
                  onClick={() => navigate(`/crm/tarefas/${tarefa.id}`)}
                />
              ))
            ) : (
              <EmptyState>Nenhuma tarefa pendente</EmptyState>
            )}
          </div>
        </Panel>

        {/* Leads sem Contato */}
        <Panel
          title="Leads sem Contato"
          icon={<AlertTriangle className="h-4 w-4" />}
          action={<PanelLink onClick={() => navigate('/crm')}>Ver CRM</PanelLink>}
          delay={520}
        >
          <div className="space-y-1.5">
            {data?.leadsSemContato && data.leadsSemContato.length > 0 ? (
              data.leadsSemContato.map((lead) => (
                <LeadItem
                  key={lead.leadId}
                  lead={lead}
                  onClick={() => navigate(`/crm/leads/${lead.leadId}`)}
                />
              ))
            ) : (
              <EmptyState>Todos os leads estão em dia!</EmptyState>
            )}
          </div>
        </Panel>

        {/* Tempo no Estoque */}
        <Panel title="Tempo no Estoque" icon={<Clock className="h-4 w-4" />} delay={580}>
          <div className="space-y-3.5">
            {buckets.map((item, i) => (
              <div key={item.periodo} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 font-medium text-slate-600">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: agingColors[i % agingColors.length] }}
                    />
                    {item.periodo}
                  </span>
                  <span className="font-bold tabular-nums text-slate-900">{item.quantidade}</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(item.quantidade / maxBucket) * 100}%`,
                      backgroundColor: agingColors[i % agingColors.length],
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Tabelas - Veículos Recentes e Ranking */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Veículos Recentes */}
        <Panel
          title="Veículos Recentes"
          icon={<Car className="h-4 w-4" />}
          action={<PanelLink onClick={() => navigate('/veiculos')}>Ver todos</PanelLink>}
          delay={640}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pb-3 font-bold">Veículo</th>
                  <th className="pb-3 font-bold">Placa</th>
                  <th className="pb-3 text-right font-bold">Valor</th>
                </tr>
              </thead>
              <tbody>
                {data?.veiculosRecentes && data.veiculosRecentes.length > 0 ? (
                  data.veiculosRecentes.map((veiculo) => (
                    <tr
                      key={veiculo.id}
                      className="group cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50"
                      onClick={() => navigate(`/veiculos/${veiculo.id}`)}
                    >
                      <td className="py-3 font-semibold text-slate-800 group-hover:text-primary-600">
                        {veiculo.marca} {veiculo.modelo}
                      </td>
                      <td className="py-3 font-mono text-slate-500">{veiculo.placa || '-'}</td>
                      <td className="py-3 text-right font-bold tabular-nums text-slate-900">
                        {formatCurrency(veiculo.valorVenda)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <EmptyState>Nenhum veículo cadastrado</EmptyState>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* Ranking Vendedores */}
        <Panel title="Ranking de Vendedores" icon={<TrendingUp className="h-4 w-4" />} delay={700}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pb-3 font-bold">#</th>
                  <th className="pb-3 font-bold">Vendedor</th>
                  <th className="pb-3 text-center font-bold">Mês Atual</th>
                  <th className="pb-3 text-center font-bold">Anterior</th>
                </tr>
              </thead>
              <tbody>
                {data?.rankingVendedores && data.rankingVendedores.length > 0 ? (
                  data.rankingVendedores.map((vendedor, index) => (
                    <tr
                      key={vendedor.vendedorId}
                      className="border-b border-slate-50 last:border-0"
                    >
                      <td className="py-3">
                        <span
                          className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ring-1 ${
                            index === 0
                              ? 'bg-amber-50 text-amber-600 ring-amber-200'
                              : index === 1
                                ? 'bg-slate-100 text-slate-500 ring-slate-200'
                                : index === 2
                                  ? 'bg-orange-50 text-orange-600 ring-orange-200'
                                  : 'bg-slate-50 text-slate-400 ring-slate-100'
                          }`}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-3 font-semibold text-slate-800">{vendedor.nome}</td>
                      <td className="py-3 text-center font-bold tabular-nums text-slate-900">
                        {vendedor.vendasMesAtual}
                      </td>
                      <td className="py-3 text-center tabular-nums text-slate-400">
                        {vendedor.vendasMesAnterior}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <EmptyState>Nenhuma venda registrada</EmptyState>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}

// ==========================================
// FUNÇÕES AUXILIARES
// ==========================================

function calcularTrend(atual: number, anterior: number): number {
  if (anterior === 0) return atual > 0 ? 100 : 0
  return Math.round(((atual - anterior) / anterior) * 100)
}
